// The fields law review package F added to the data protection registers (backend lib/ropaParticulars.ts,
// lib/dpiaWorkflow.ts, lib/breachContent.ts, lib/rightsRequests.ts, lib/dpAgreements.ts, lib/dpIdentity.ts), and
// the two places that are new: the organisation's data protection identity and the agreements register. The
// server decides and refuses; these forms only ask, and show a field only where the record makes it relevant.

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiErrorMessage } from "../api/client";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle, type as text } from "../theme";
import { whoCan } from "../lib/permissions";
import { ModalOverlay } from "../pages/resilience/shared";
import { DetailFields, DetailSection, EditButton } from "./RecordView";
import { NextSteps, type NextStepItem } from "./NextSteps";
import { toDateInput, dateInputToIso, fmtDateOnly } from "../lib/dateOnly";
import { isSensitiveCategory, isSpecialCategory } from "../lib/dataCategories";
import { ICO_HIGH_RISK_LIST } from "../lib/dpiaCriteria";
import {
  ART10_BASES, ART34_EXEMPTIONS, ART49_DEROGATIONS, ART9_CONDITIONS, BREACH_ORG_ROLES, DP_AGREEMENT_KINDS,
  RECIPIENTS_NOTIFIED, TRA_MECHANISMS, TRANSFER_MECHANISMS, UK_SCHEDULE1_CONDITIONS, dpAgreementKindLabel,
} from "../lib/dpParticulars";

const field: React.CSSProperties = { ...inputStyle, fontSize: 13 };
const area: React.CSSProperties = { ...inputStyle, fontSize: 13, resize: "vertical" };

export function Field({ label, hint, children, span2 = true }: { label: string; hint?: string; children: React.ReactNode; span2?: boolean }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, gridColumn: span2 ? "1 / -1" : undefined }}>
      <span style={{ fontWeight: 500, color: theme.color.text }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{hint}</span>}
    </label>
  );
}

export function ChipSelect({ options, selected, onChange }: { options: ReadonlyArray<{ id: string; label: string }>; selected: string[]; onChange: (v: string[]) => void }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button key={o.id} type="button" aria-pressed={on} onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}
            style={{ padding: "4px 10px", borderRadius: 3, fontSize: 12, cursor: "pointer", textAlign: "left",
              border: on ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
              background: on ? "var(--c-accentBg)" : "var(--c-panel)", color: on ? "var(--c-accentText)" : theme.color.muted, fontWeight: on ? 600 : 400 }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A yes / no question that may also be unanswered: null is "Not answered", never read as no. */
function TriState({ value, onChange, yes = "Yes", no = "No" }: { value: boolean | null; onChange: (v: boolean | null) => void; yes?: string; no?: string }) {
  return (
    <select value={value === null ? "" : value ? "yes" : "no"} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "yes")} style={field}>
      <option value="">Not answered</option>
      <option value="yes">{yes}</option>
      <option value="no">{no}</option>
    </select>
  );
}

const Note = ({ children }: { children: React.ReactNode }) => (
  <div style={{ gridColumn: "1 / -1", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{children}</div>
);

// ── RoPA particulars ─────────────────────────────────────────────────────────

export interface RopaParticularsForm {
  controllerName: string;
  controllerContact: string;
  securityMeasures: string;
  specialCategoryConditions: string[];
  criminalDataBasis: string;
  ukSchedule1Conditions: string[];
  appropriatePolicyDocument: boolean | null;
  apdRetentionFollowed: boolean | null;
  apdRetentionNote: string;
  liaPurpose: string;
  liaNecessity: string;
  liaBalancing: string;
  liaAssessedAt: string;
  consentRecordLocation: string;
  transferMechanism: string;
  transferDerogation: string;
  transferDerogationAssessment: string;
  transferDerogationAuthorityToldAt: string;
  ukHighRiskTypes: string[];
  agreementIds: string[];
}

/** The stored row as form values (date-only fields through toDateInput). */
export function ropaParticularsFrom(e: Partial<Record<keyof RopaParticularsForm, unknown>> | null | undefined): RopaParticularsForm {
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  const a = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
  const b = (v: unknown) => (typeof v === "boolean" ? v : null);
  return {
    controllerName: s(e?.controllerName), controllerContact: s(e?.controllerContact), securityMeasures: s(e?.securityMeasures),
    specialCategoryConditions: a(e?.specialCategoryConditions), criminalDataBasis: s(e?.criminalDataBasis),
    ukSchedule1Conditions: a(e?.ukSchedule1Conditions), appropriatePolicyDocument: b(e?.appropriatePolicyDocument),
    apdRetentionFollowed: b(e?.apdRetentionFollowed), apdRetentionNote: s(e?.apdRetentionNote),
    liaPurpose: s(e?.liaPurpose), liaNecessity: s(e?.liaNecessity), liaBalancing: s(e?.liaBalancing),
    liaAssessedAt: toDateInput((e?.liaAssessedAt as string | null) ?? null), consentRecordLocation: s(e?.consentRecordLocation),
    transferMechanism: s(e?.transferMechanism), transferDerogation: s(e?.transferDerogation),
    transferDerogationAssessment: s(e?.transferDerogationAssessment),
    transferDerogationAuthorityToldAt: toDateInput((e?.transferDerogationAuthorityToldAt as string | null) ?? null),
    ukHighRiskTypes: a(e?.ukHighRiskTypes), agreementIds: a(e?.agreementIds),
  };
}

/** What the API takes: blanks as null, dates as yyyy-mm-dd (UTC midnight on the server). */
export function ropaParticularsPayload(f: RopaParticularsForm): Record<string, unknown> {
  const t = (v: string) => (v.trim() ? v.trim() : null);
  return {
    controllerName: t(f.controllerName), controllerContact: t(f.controllerContact), securityMeasures: t(f.securityMeasures),
    specialCategoryConditions: f.specialCategoryConditions, criminalDataBasis: f.criminalDataBasis || null,
    ukSchedule1Conditions: f.ukSchedule1Conditions, appropriatePolicyDocument: f.appropriatePolicyDocument,
    apdRetentionFollowed: f.apdRetentionFollowed, apdRetentionNote: t(f.apdRetentionNote),
    liaPurpose: t(f.liaPurpose), liaNecessity: t(f.liaNecessity), liaBalancing: t(f.liaBalancing), liaAssessedAt: f.liaAssessedAt || null,
    consentRecordLocation: t(f.consentRecordLocation), transferMechanism: f.transferMechanism || null,
    transferDerogation: f.transferMechanism === "derogation" ? f.transferDerogation || null : null,
    transferDerogationAssessment: t(f.transferDerogationAssessment),
    transferDerogationAuthorityToldAt: f.transferDerogationAuthorityToldAt || null,
    ukHighRiskTypes: f.ukHighRiskTypes, agreementIds: f.agreementIds,
  };
}

export interface AgreementOption { id: string; kind: string; title: string; counterparty: string; status: string }

/** Loads the agreements a record may link to (GET /gdpr/agreements). */
export function useAgreementOptions(): AgreementOption[] {
  return useReloadableAgreementOptions().rows;
}

/** The same, with a reload for a form that adds an agreement to the register while it is open. */
export function useReloadableAgreementOptions(): { rows: AgreementOption[]; reload: () => void } {
  const [rows, setRows] = useState<AgreementOption[]>([]);
  const reload = useCallback(() => {
    api.get<{ agreements: AgreementOption[] }>("/gdpr/agreements").then((d) => setRows(d.agreements ?? [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { rows, reload };
}

export function RopaParticularsFields({ value, onChange, role, lawfulBases, dataCategories, thirdCountryTransfer, ukInScope, agreements, recipients = [], canAddAgreement = false, onAgreementAdded }: {
  value: RopaParticularsForm;
  onChange: (patch: Partial<RopaParticularsForm>) => void;
  role: string;
  lawfulBases: string[];
  dataCategories: string[];
  thirdCountryTransfer: boolean;
  ukInScope: boolean;
  agreements: AgreementOption[];
  /** Recipients named on the activity: a recipient that is not a processor is shared with under an agreement. */
  recipients?: string[];
  /** The member may write the agreements register (privacy:write); otherwise the add choice is not offered. */
  canAddAgreement?: boolean;
  /** Called after an agreement was added from here, so the options list can be read again. */
  onAgreementAdded?: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const special = dataCategories.some(isSpecialCategory);
  const criminal = dataCategories.includes("criminal_offence");
  const sensitive = dataCategories.some(isSensitiveCategory);
  const agreementOptions = agreements.map((a) => ({ id: a.id, label: `${a.title} (${dpAgreementKindLabel(a.kind).toLowerCase()}, ${a.counterparty})${a.status === "ended" ? " - ended" : ""}` }));
  return (
    <>
      {role === "processor" && (
        <>
          <Field label="Controller it processes for *" span2={false} hint="Art. 30(2)(a): each controller the organisation acts for.">
            <input value={value.controllerName} onChange={(e) => onChange({ controllerName: e.target.value })} maxLength={300} style={field} placeholder="e.g. Client plc" />
          </Field>
          <Field label="Controller contact" span2={false}>
            <input value={value.controllerContact} onChange={(e) => onChange({ controllerContact: e.target.value })} maxLength={1000} style={field} placeholder="Name, email or address" />
          </Field>
        </>
      )}
      <Field label="Security measures" hint="Art. 30(1)(g): a general description of the technical and organisational measures that protect this processing.">
        <textarea rows={2} value={value.securityMeasures} onChange={(e) => onChange({ securityMeasures: e.target.value })} style={area}
          placeholder="e.g. Role-based access, encryption at rest, quarterly access review" />
      </Field>
      {role !== "processor" && lawfulBases.includes("legitimate_interests") && (
        <>
          <Note>Legitimate interests assessment, recorded before the processing starts (ICO; EDPB Guidelines 1/2024).</Note>
          <Field label="Purpose test: the interest pursued" span2={false}>
            <textarea rows={2} value={value.liaPurpose} onChange={(e) => onChange({ liaPurpose: e.target.value })} style={area} />
          </Field>
          <Field label="Necessity test" span2={false}>
            <textarea rows={2} value={value.liaNecessity} onChange={(e) => onChange({ liaNecessity: e.target.value })} style={area} />
          </Field>
          <Field label="Balancing test" span2={false}>
            <textarea rows={2} value={value.liaBalancing} onChange={(e) => onChange({ liaBalancing: e.target.value })} style={area} />
          </Field>
          <Field label="Assessed on" span2={false}>
            <input type="date" value={value.liaAssessedAt} onChange={(e) => onChange({ liaAssessedAt: e.target.value })} style={field} />
          </Field>
        </>
      )}
      {role !== "processor" && lawfulBases.includes("consent") && (
        <Field label="Where the consent records are kept" hint="The organisation must be able to show that each person consented (Art. 7(1)).">
          <input value={value.consentRecordLocation} onChange={(e) => onChange({ consentRecordLocation: e.target.value })} style={field} placeholder="e.g. Marketing platform consent log" />
        </Field>
      )}
      {role !== "processor" && special && (
        <Field label="Art. 9(2) condition(s) for the special category data">
          <ChipSelect options={ART9_CONDITIONS} selected={value.specialCategoryConditions} onChange={(v) => onChange({ specialCategoryConditions: v })} />
        </Field>
      )}
      {role !== "processor" && criminal && (
        <Field label="Art. 10 basis for criminal offence data">
          <select value={value.criminalDataBasis} onChange={(e) => onChange({ criminalDataBasis: e.target.value })} style={field}>
            <option value="">Not recorded</option>
            {ART10_BASES.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </Field>
      )}
      {role !== "processor" && ukInScope && sensitive && (
        <>
          <Field label="DPA 2018 Schedule 1 condition(s)" hint="Under UK law Art. 9(2)(b), (g), (h), (i) and (j) and criminal offence data are met through a Schedule 1 paragraph.">
            <ChipSelect options={UK_SCHEDULE1_CONDITIONS} selected={value.ukSchedule1Conditions} onChange={(v) => onChange({ ukSchedule1Conditions: v })} />
          </Field>
          <Field label="Covered by an appropriate policy document?" span2={false} hint="Needed for para 1 and most Part 2 conditions (DPA 2018 Sch. 1 Part 4).">
            <TriState value={value.appropriatePolicyDocument} onChange={(v) => onChange({ appropriatePolicyDocument: v })} yes="Yes" no="No, not needed for this condition" />
          </Field>
          {value.appropriatePolicyDocument === true && (
            <Field label="Retained and erased in line with it?" span2={false} hint="Sch. 1 para 41(c).">
              <TriState value={value.apdRetentionFollowed} onChange={(v) => onChange({ apdRetentionFollowed: v })} />
            </Field>
          )}
          {value.appropriatePolicyDocument === true && value.apdRetentionFollowed === false && (
            <Field label="Why not">
              <input value={value.apdRetentionNote} onChange={(e) => onChange({ apdRetentionNote: e.target.value })} style={field} />
            </Field>
          )}
        </>
      )}
      {role !== "processor" && thirdCountryTransfer && (
        <>
          <Field label="Transfer mechanism" span2={false}>
            <select value={value.transferMechanism} onChange={(e) => onChange({ transferMechanism: e.target.value })} style={field}>
              <option value="">Not recorded</option>
              {TRANSFER_MECHANISMS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </Field>
          {value.transferMechanism === "derogation" && (
            <Field label="Art. 49(1) derogation" span2={false}>
              <select value={value.transferDerogation} onChange={(e) => onChange({ transferDerogation: e.target.value })} style={field}>
                <option value="">Not recorded</option>
                {ART49_DEROGATIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </Field>
          )}
          {value.transferMechanism === "derogation" && value.transferDerogation === "compelling_legitimate_interests" && (
            <>
              <Field label="Assessment and safeguards" hint="Art. 49(1) second subparagraph and 49(6): documented in the record; the authority is informed and the person told.">
                <textarea rows={3} value={value.transferDerogationAssessment} onChange={(e) => onChange({ transferDerogationAssessment: e.target.value })} style={area} />
              </Field>
              <Field label="Authority informed on" span2={false}>
                <input type="date" value={value.transferDerogationAuthorityToldAt} onChange={(e) => onChange({ transferDerogationAuthorityToldAt: e.target.value })} style={field} />
              </Field>
            </>
          )}
          {TRA_MECHANISMS.has(value.transferMechanism) && (
            <Note>Link the transfer risk assessment below (record it under Agreements first).</Note>
          )}
        </>
      )}
      {role !== "processor" && ukInScope && (
        <Field label="ICO high-risk processing (UK Art. 35(4) list)" hint="Any of these makes a DPIA required under UK law; innovative technology, biometric and genetic data only together with another WP248 criterion.">
          <ChipSelect options={ICO_HIGH_RISK_LIST.map((i) => ({ id: i.id, label: i.label }))} selected={value.ukHighRiskTypes} onChange={(v) => onChange({ ukHighRiskTypes: v })} />
        </Field>
      )}
      <Field label="Agreements this activity relies on" hint={agreementHint(role, recipients)}>
        {agreementOptions.length
          ? <ChipSelect options={agreementOptions} selected={value.agreementIds} onChange={(v) => onChange({ agreementIds: v })} />
          : <span style={{ fontSize: 12, color: theme.color.subtle }}>No agreements recorded yet.{canAddAgreement ? " Add one here, or under Agreements, to link it." : " Add them under Agreements to link them here."}</span>}
      </Field>
      {(canAddAgreement || !agreementOptions.length) && (
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {canAddAgreement
            ? <button type="button" onClick={() => setAdding(true)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Add an agreement</button>
            : <span style={{ fontSize: 12, color: theme.color.subtle }}>{whoCan("privacy:write", "add one")}</span>}
        </div>
      )}
      {adding && (
        <AgreementFormModal
          id={null}
          initial={{ ...emptyAgreement, kind: defaultAgreementKind(role, recipients), counterparty: role === "processor" ? value.controllerName : "" }}
          onClose={() => setAdding(false)}
          onSaved={(a) => {
            setAdding(false);
            if (a?.id && !value.agreementIds.includes(a.id)) onChange({ agreementIds: [...value.agreementIds, a.id] });
            onAgreementAdded?.();
          }}
        />
      )}
    </>
  );
}

/** The kind of agreement an activity most likely needs from its role: Art. 26 for joint controllers, the controller's agreement for a processor, otherwise sharing with a recipient. */
export function defaultAgreementKind(role: string, recipients: readonly string[]): string {
  if (role === "joint_controller") return "joint_controller_arrangement";
  if (role === "processor") return "processor_agreement";
  return recipients.length ? "data_sharing" : "joint_controller_arrangement";
}

function agreementHint(role: string, recipients: readonly string[]): string {
  if (role === "joint_controller") return "Link the Art. 26 joint controller arrangement.";
  if (role === "processor") return "Link the agreement the controller holds you to (you are its processor).";
  const shared = recipients.length ? ` A recipient that is not your processor (${recipients.slice(0, 3).join(", ")}${recipients.length > 3 ? ", ..." : ""}) is shared with under a data sharing agreement.` : "";
  return `Joint controller arrangements, data sharing agreements, transfer tools and transfer risk assessments. Agreements with your own processors stay on each supplier.${shared}`;
}

/** The particulars as read-only rows for an activity's view. */
export function ropaParticularsDetail(e: Partial<RopaParticularsForm> & { controllerProcessor?: string; agreements?: AgreementOption[] }, labels: (ids: string[], list: ReadonlyArray<{ id: string; label: string }>) => string) {
  return [
    ...(e.controllerProcessor === "processor" ? [{ label: "Controller it processes for", value: [e.controllerName, e.controllerContact].filter(Boolean).join(", ") || null }] : []),
    { label: "Security measures", value: e.securityMeasures || null, wide: true, multiline: true },
    ...(e.specialCategoryConditions?.length ? [{ label: "Art. 9(2) conditions", value: labels(e.specialCategoryConditions, ART9_CONDITIONS) }] : []),
    ...(e.criminalDataBasis ? [{ label: "Art. 10 basis", value: labels([e.criminalDataBasis], ART10_BASES) }] : []),
    ...(e.ukSchedule1Conditions?.length ? [{ label: "Schedule 1 conditions", value: labels(e.ukSchedule1Conditions, UK_SCHEDULE1_CONDITIONS) }] : []),
    ...(e.transferMechanism ? [{ label: "Transfer mechanism", value: labels([e.transferMechanism], TRANSFER_MECHANISMS) }] : []),
    ...(e.agreements?.length ? [{ label: "Agreements", value: e.agreements.map((a) => `${a.title} (${dpAgreementKindLabel(a.kind).toLowerCase()})`).join("; "), wide: true }] : []),
  ];
}

// ── Identity (Art. 30(1)(a)) ─────────────────────────────────────────────────

interface DpIdentity {
  controllerAddress: string | null; controllerContact: string | null; representativeName: string | null;
  representativeContact: string | null; dpoName: string | null; dpoContact: string | null;
}
const IDENTITY_FIELDS: Array<{ key: keyof DpIdentity; label: string; hint?: string }> = [
  { key: "controllerAddress", label: "Registered or principal address" },
  { key: "controllerContact", label: "Data protection contact (email or telephone)" },
  { key: "representativeName", label: "Representative in the EU or UK (Art. 27)", hint: "Only where the organisation is established outside it." },
  { key: "representativeContact", label: "Representative contact" },
  { key: "dpoName", label: "Data protection officer", hint: "Leave empty if no DPO is designated." },
  { key: "dpoContact", label: "DPO contact" },
];

/** The organisation's controller identity, as the record of processing and a breach notification name it. */
export function DpIdentityPanel({ canEdit, startOpen }: { canEdit: boolean; startOpen?: boolean }) {
  const [data, setData] = useState<{ name: string; identity: DpIdentity; profileDpo: string | null } | null>(null);
  const [editing, setEditing] = useState<DpIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get<{ name: string; identity: DpIdentity; profileDpo: string | null }>("/gdpr/identity").then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (startOpen && data && canEdit) setEditing(data.identity); }, [startOpen, data, canEdit]);
  if (!data) return null;
  const id = data.identity;
  const missing = !(id.controllerAddress || id.controllerContact);
  async function save() {
    if (!editing) return;
    setSaving(true); setError(null);
    try { await api.put("/gdpr/identity", editing); setEditing(null); load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not save the identity")); }
    finally { setSaving(false); }
  }
  return (
    <DetailSection title="Data protection identity" aside={canEdit ? <EditButton onClick={() => setEditing(id)} label={missing ? "Add details" : "Edit"} /> : undefined}>
      {missing && (
        <NextSteps
          items={[{ done: false, label: "Record the organisation's address or data protection contact", hint: "The record of processing starts with the controller's name and contact details (GDPR Art. 30(1)(a))." }]}
          action={canEdit ? <button type="button" onClick={() => setEditing(id)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Fill these in</button> : undefined}
        />
      )}
      <DetailFields fields={[
        { label: "Controller", value: data.name },
        ...IDENTITY_FIELDS.map((f) => ({ label: f.label, value: id[f.key] ?? (f.key === "dpoName" ? data.profileDpo : null) })),
      ]} />
      {editing && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1 / -1", fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Data protection identity</div>
            {IDENTITY_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} hint={f.hint} span2={false}>
                <input value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} style={field} />
              </Field>
            ))}
            {error && <div role="alert" style={{ gridColumn: "1 / -1", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>}
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
              <button type="button" onClick={save} disabled={saving} style={btnPrimary}>{saving ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </DetailSection>
  );
}

// ── DPIA ─────────────────────────────────────────────────────────────────────

export interface DpiaLawForm {
  dpoAdviceFollowed: boolean | null; dpoDisagreementReason: string;
  subjectViewsSought: boolean | null; subjectViews: string; subjectViewsNotSoughtReason: string;
  priorConsultationAt: string; priorConsultationAuthority: string; priorConsultationExtended: boolean;
  priorConsultationReplyAt: string; priorConsultationReply: string;
}

export function dpiaLawFrom(d: Partial<Record<keyof DpiaLawForm, unknown>>): DpiaLawForm {
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  const b = (v: unknown) => (typeof v === "boolean" ? v : null);
  const day = (v: unknown) => (typeof v === "string" && v ? v.slice(0, 10) : "");
  return {
    dpoAdviceFollowed: b(d.dpoAdviceFollowed), dpoDisagreementReason: s(d.dpoDisagreementReason),
    subjectViewsSought: b(d.subjectViewsSought), subjectViews: s(d.subjectViews), subjectViewsNotSoughtReason: s(d.subjectViewsNotSoughtReason),
    priorConsultationAt: day(d.priorConsultationAt), priorConsultationAuthority: s(d.priorConsultationAuthority),
    priorConsultationExtended: d.priorConsultationExtended === true, priorConsultationReplyAt: day(d.priorConsultationReplyAt),
    priorConsultationReply: s(d.priorConsultationReply),
  };
}

/** Consultation dates are instants on the server; a day picked here is that day at UTC midnight. */
export function dpiaLawPayload(f: DpiaLawForm): Record<string, unknown> {
  const t = (v: string) => (v.trim() ? v.trim() : null);
  return {
    dpoAdviceFollowed: f.dpoAdviceFollowed, dpoDisagreementReason: t(f.dpoDisagreementReason),
    subjectViewsSought: f.subjectViewsSought, subjectViews: t(f.subjectViews), subjectViewsNotSoughtReason: t(f.subjectViewsNotSoughtReason),
    priorConsultationAt: f.priorConsultationAt ? dateInputToIso(f.priorConsultationAt) : null,
    priorConsultationAuthority: t(f.priorConsultationAuthority), priorConsultationExtended: f.priorConsultationExtended,
    priorConsultationReplyAt: f.priorConsultationReplyAt ? dateInputToIso(f.priorConsultationReplyAt) : null,
    priorConsultationReply: t(f.priorConsultationReply),
  };
}

export function DpiaLawFields({ value, onChange, dpoConsulted, residualRisk }: { value: DpiaLawForm; onChange: (p: Partial<DpiaLawForm>) => void; dpoConsulted: boolean; residualRisk: string }) {
  return (
    <>
      {dpoConsulted && (
        <Field label="Was the DPO's advice followed?" span2={false} hint="Art. 35(2); where it was not, record why.">
          <TriState value={value.dpoAdviceFollowed} onChange={(v) => onChange({ dpoAdviceFollowed: v })} />
        </Field>
      )}
      {dpoConsulted && value.dpoAdviceFollowed === false && (
        <Field label="Why the advice was not followed" span2={false}>
          <textarea rows={2} value={value.dpoDisagreementReason} onChange={(e) => onChange({ dpoDisagreementReason: e.target.value })} style={area} />
        </Field>
      )}
      <Field label="Were the views of data subjects sought?" span2={false} hint="Art. 35(9): their views or their representatives', or why they were not sought.">
        <TriState value={value.subjectViewsSought} onChange={(v) => onChange({ subjectViewsSought: v })} />
      </Field>
      {value.subjectViewsSought === true && (
        <Field label="Their views" span2={false}>
          <textarea rows={2} value={value.subjectViews} onChange={(e) => onChange({ subjectViews: e.target.value })} style={area} />
        </Field>
      )}
      {value.subjectViewsSought === false && (
        <Field label="Why they were not sought" span2={false}>
          <textarea rows={2} value={value.subjectViewsNotSoughtReason} onChange={(e) => onChange({ subjectViewsNotSoughtReason: e.target.value })} style={area} />
        </Field>
      )}
      {residualRisk === "high" && (
        <>
          <Note>Residual risk is high: the supervisory authority must be consulted before the processing (Art. 36(1)). It has eight weeks to give written advice, extendable by six (Art. 36(2)); the DPIA can be approved once it has replied or that period has ended.</Note>
          <Field label="Consulted on" span2={false}>
            <input type="date" value={value.priorConsultationAt} onChange={(e) => onChange({ priorConsultationAt: e.target.value })} style={field} />
          </Field>
          <Field label="Authority" span2={false}>
            <input value={value.priorConsultationAuthority} onChange={(e) => onChange({ priorConsultationAuthority: e.target.value })} style={field} placeholder="e.g. the ICO" />
          </Field>
          <Field label="The authority extended its period by six weeks" span2={false}>
            <input type="checkbox" checked={value.priorConsultationExtended} onChange={(e) => onChange({ priorConsultationExtended: e.target.checked })} />
          </Field>
          <Field label="Reply received on" span2={false}>
            <input type="date" value={value.priorConsultationReplyAt} onChange={(e) => onChange({ priorConsultationReplyAt: e.target.value })} style={field} />
          </Field>
          <Field label="The authority's advice">
            <textarea rows={2} value={value.priorConsultationReply} onChange={(e) => onChange({ priorConsultationReply: e.target.value })} style={area} />
          </Field>
        </>
      )}
    </>
  );
}

// ── Breach ───────────────────────────────────────────────────────────────────

export interface BreachLawForm {
  orgRole: string; affectedControllers: string; controllerNotifiedAt: string;
  subjectCategories: string; approxRecords: string; dpoContact: string; likelyConsequences: string; delayReasons: string;
  notNotifiedReason: string; subjectsExemption: string; subjectsExemptionNote: string; agreementIds: string[];
}

export function breachLawFrom(b: Partial<Record<keyof BreachLawForm, unknown>> & { agreementIds?: string[] }): BreachLawForm {
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  return {
    orgRole: s(b.orgRole) || "controller", affectedControllers: Array.isArray(b.affectedControllers) ? (b.affectedControllers as string[]).join(", ") : "",
    controllerNotifiedAt: typeof b.controllerNotifiedAt === "string" ? b.controllerNotifiedAt.slice(0, 16) : "",
    subjectCategories: Array.isArray(b.subjectCategories) ? (b.subjectCategories as string[]).join(", ") : "",
    approxRecords: b.approxRecords == null ? "" : String(b.approxRecords), dpoContact: s(b.dpoContact), likelyConsequences: s(b.likelyConsequences),
    delayReasons: s(b.delayReasons), notNotifiedReason: s(b.notNotifiedReason), subjectsExemption: s(b.subjectsExemption),
    subjectsExemptionNote: s(b.subjectsExemptionNote), agreementIds: b.agreementIds ?? [],
  };
}

/** `lockedController`: the controller notification is recorded and is final (CLAUDE.md pattern 7), so it is not sent again. */
export function breachLawPayload(f: BreachLawForm, lockedController: boolean): Record<string, unknown> {
  const t = (v: string) => (v.trim() ? v.trim() : null);
  const list = (v: string) => v.split(",").map((x) => x.trim()).filter(Boolean);
  return {
    orgRole: f.orgRole, affectedControllers: list(f.affectedControllers),
    ...(lockedController ? {} : { controllerNotifiedAt: f.controllerNotifiedAt ? new Date(f.controllerNotifiedAt).toISOString() : null }),
    subjectCategories: list(f.subjectCategories), approxRecords: f.approxRecords === "" ? null : Math.max(0, Math.round(Number(f.approxRecords))),
    dpoContact: t(f.dpoContact), likelyConsequences: t(f.likelyConsequences), delayReasons: t(f.delayReasons),
    notNotifiedReason: t(f.notNotifiedReason), subjectsExemption: f.subjectsExemption || null, subjectsExemptionNote: t(f.subjectsExemptionNote),
    agreementIds: f.agreementIds,
  };
}

export function BreachLawFields({ value, onChange, notifiedDpaRequired, severity, lockedController, agreements }: {
  value: BreachLawForm; onChange: (p: Partial<BreachLawForm>) => void; notifiedDpaRequired: boolean; severity: string; lockedController: boolean; agreements: AgreementOption[];
}) {
  const asProcessor = value.orgRole === "processor" || value.orgRole === "both";
  const asController = value.orgRole !== "processor";
  const processorAgreements = agreements.filter((a) => a.kind === "processor_agreement").map((a) => ({ id: a.id, label: `${a.title} (${a.counterparty})` }));
  return (
    <>
      <Field label="Our role for this data" hint="As processor the organisation tells each controller without undue delay; whether the authority and the people affected are told is the controller's decision (Art. 33(2)).">
        <select value={value.orgRole} onChange={(e) => onChange({ orgRole: e.target.value })} style={field}>
          {BREACH_ORG_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
      </Field>
      {asProcessor && (
        <>
          <Field label="Controllers affected (comma-separated)" span2={false}>
            <input value={value.affectedControllers} onChange={(e) => onChange({ affectedControllers: e.target.value })} style={field} />
          </Field>
          <Field label="Controller told on" span2={false} hint={lockedController ? "Recorded, and final." : undefined}>
            <input type="datetime-local" disabled={lockedController} value={value.controllerNotifiedAt} onChange={(e) => onChange({ controllerNotifiedAt: e.target.value })} style={field} />
          </Field>
          {processorAgreements.length > 0 && (
            <Field label="Processor agreements (their breach window applies)">
              <ChipSelect options={processorAgreements} selected={value.agreementIds} onChange={(v) => onChange({ agreementIds: v })} />
            </Field>
          )}
        </>
      )}
      {asController && (
        <>
          <Note>What a notification to the authority must describe (Art. 33(3)); approximate figures are enough.</Note>
          <Field label="Categories of data subjects (comma-separated)" span2={false}>
            <input value={value.subjectCategories} onChange={(e) => onChange({ subjectCategories: e.target.value })} style={field} placeholder="e.g. customers, employees" />
          </Field>
          <Field label="Approximate number of records" span2={false}>
            <input type="number" min={0} value={value.approxRecords} onChange={(e) => onChange({ approxRecords: e.target.value })} style={field} />
          </Field>
          <Field label="DPO or other contact point" span2={false}>
            <input value={value.dpoContact} onChange={(e) => onChange({ dpoContact: e.target.value })} style={field} />
          </Field>
          <Field label="Likely consequences" span2={false}>
            <textarea rows={2} value={value.likelyConsequences} onChange={(e) => onChange({ likelyConsequences: e.target.value })} style={area} />
          </Field>
          {notifiedDpaRequired
            ? (
              <Field label="Reasons for any delay" hint="A notification after the deadline must carry them (Art. 33(1)).">
                <textarea rows={2} value={value.delayReasons} onChange={(e) => onChange({ delayReasons: e.target.value })} style={area} />
              </Field>
            ) : (
              <Field label="Why the authority is not being told" hint="Every breach is documented, including the decision not to notify (Art. 33(5)).">
                <textarea rows={2} value={value.notNotifiedReason} onChange={(e) => onChange({ notNotifiedReason: e.target.value })} style={area} />
              </Field>
            )}
          {(severity === "high" || severity === "critical") && (
            <>
              <Field label="Not telling the people affected because" span2={false} hint="Art. 34(3): leave empty if they are being told.">
                <select value={value.subjectsExemption} onChange={(e) => onChange({ subjectsExemption: e.target.value })} style={field}>
                  <option value="">They are being told</option>
                  {ART34_EXEMPTIONS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
              </Field>
              {value.subjectsExemption && (
                <Field label="Why the exemption applies" span2={false}>
                  <textarea rows={2} value={value.subjectsExemptionNote} onChange={(e) => onChange({ subjectsExemptionNote: e.target.value })} style={area} />
                </Field>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

// ── Requests ─────────────────────────────────────────────────────────────────
// The UK GDPR Art. 12A stops of the clock and the extension are actions on the request (Stop the clock / Restart
// the clock / Extend in GdprPage.tsx, POST /gdpr/dsr/:id/pause|resume, PATCH /gdpr/dsr/:id/extend), never form fields.

export interface DsrLawForm {
  recipientsNotified: string; recipientsNotifiedNote: string;
}

export function dsrLawFrom(r: Partial<Record<keyof DsrLawForm, unknown>>): DsrLawForm {
  return {
    recipientsNotified: typeof r.recipientsNotified === "string" ? r.recipientsNotified : "",
    recipientsNotifiedNote: typeof r.recipientsNotifiedNote === "string" ? r.recipientsNotifiedNote : "",
  };
}

export function dsrLawPayload(f: DsrLawForm): Record<string, unknown> {
  return {
    recipientsNotified: f.recipientsNotified || null,
    recipientsNotifiedNote: f.recipientsNotifiedNote.trim() || null,
  };
}

/** Art. 19: a rectification, erasure or restriction closes with whether the recipients were told. */
export function DsrLawFields({ value, onChange, type }: { value: DsrLawForm; onChange: (p: Partial<DsrLawForm>) => void; type: string }) {
  if (!["rectification", "erasure", "restriction"].includes(type)) return null;
  return (
    <>
      <Field label="Recipients told of the outcome?" span2={false} hint="Art. 19: needed before the request is completed.">
        <select value={value.recipientsNotified} onChange={(e) => onChange({ recipientsNotified: e.target.value })} style={field}>
          <option value="">Not recorded</option>
          {RECIPIENTS_NOTIFIED.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
      </Field>
      {value.recipientsNotified === "impossible_or_disproportionate" && (
        <Field label="Why" span2={false}>
          <input value={value.recipientsNotifiedNote} onChange={(e) => onChange({ recipientsNotifiedNote: e.target.value })} style={field} />
        </Field>
      )}
    </>
  );
}


// ── Agreements register ──────────────────────────────────────────────────────

interface Agreement extends AgreementOption {
  supplierId: string | null; startDate: string | null; endDate: string | null; reviewAt: string | null;
  responsibilities: string | null; contactPoint: string | null; essenceAvailable: boolean | null; breachNoticeHours: number | null;
  destinationCountries: string[]; assessmentOutcome: string | null; signedDocumentGroupId: string | null; notes: string | null;
  supplier: { id: string; name: string } | null; ropaEntries: Array<{ id: string; name: string }>; breaches: Array<{ id: string; title: string }>;
}

export interface AgreementForm {
  kind: string; title: string; counterparty: string; status: string; startDate: string; endDate: string; reviewAt: string;
  responsibilities: string; contactPoint: string; essenceAvailable: boolean | null; breachNoticeHours: string;
  destinationCountries: string; assessmentOutcome: string; signedDocumentGroupId: string; notes: string;
}

export const emptyAgreement: AgreementForm = {
  kind: "joint_controller_arrangement", title: "", counterparty: "", status: "active", startDate: "", endDate: "", reviewAt: "",
  responsibilities: "", contactPoint: "", essenceAvailable: null, breachNoticeHours: "", destinationCountries: "", assessmentOutcome: "",
  signedDocumentGroupId: "", notes: "",
};

/** What an agreement still lacks (mirror of backend lib/dpAgreements.ts agreementMissing). */
function agreementSteps(a: Agreement): NextStepItem[] {
  const out: NextStepItem[] = [];
  const add = (_id: string, label: string) => out.push({ done: false, label });
  if (!a.signedDocumentGroupId) add("signed", "Link the signed copy from the Evidence Repository");
  if (a.kind === "joint_controller_arrangement") {
    if (!a.responsibilities) add("resp", "Say who is responsible for what (Art. 26(1))");
    if (!a.contactPoint) add("contact", "Name a contact point for data subjects");
    if (a.essenceAvailable !== true) add("essence", "Make the essence of the arrangement available to data subjects (Art. 26(2))");
  }
  if (a.kind === "processor_agreement" && a.breachNoticeHours == null) add("window", "Record the window to report a breach to the controller");
  if ((a.kind === "transfer_tool" || a.kind === "transfer_risk_assessment") && !a.destinationCountries.length) add("dest", "Record the destination countries");
  if (a.kind === "transfer_risk_assessment" && !a.assessmentOutcome) add("outcome", "Record the outcome of the assessment");
  return out;
}

export function AgreementsTab({ canEdit, initialOpen }: { canEdit: boolean; initialOpen?: string | null }) {
  const [rows, setRows] = useState<Agreement[] | null>(null);
  const [open, setOpen] = useState<string | null>(initialOpen ?? null);
  const [editing, setEditing] = useState<{ id: string | null; form: AgreementForm } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    api.get<{ agreements: Agreement[] }>("/gdpr/agreements").then((d) => setRows(d.agreements ?? [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  function startEdit(a: Agreement | null) {
    setError(null);
    setEditing({
      id: a?.id ?? null,
      form: a ? {
        kind: a.kind, title: a.title, counterparty: a.counterparty, status: a.status, startDate: toDateInput(a.startDate), endDate: toDateInput(a.endDate),
        reviewAt: toDateInput(a.reviewAt), responsibilities: a.responsibilities ?? "", contactPoint: a.contactPoint ?? "", essenceAvailable: a.essenceAvailable,
        breachNoticeHours: a.breachNoticeHours == null ? "" : String(a.breachNoticeHours), destinationCountries: a.destinationCountries.join(", "),
        assessmentOutcome: a.assessmentOutcome ?? "", signedDocumentGroupId: a.signedDocumentGroupId ?? "", notes: a.notes ?? "",
      } : emptyAgreement,
    });
  }

  async function remove(a: Agreement) {
    if (!window.confirm(`Delete ${a.title}? Its links to processing activities and breaches go with it.`)) return;
    try { await api.del(`/gdpr/agreements/${a.id}`); load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not delete the agreement")); }
  }


  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ ...text.body, color: theme.color.muted, maxWidth: 760 }}>
          Agreements the supplier register does not hold: those under which you are the processor, joint controller arrangements, data sharing agreements, transfer tools and transfer risk assessments. Agreements with your own processors stay on each supplier.
        </div>
        {canEdit && <button type="button" onClick={() => startEdit(null)} style={btnPrimary}>Add agreement</button>}
      </div>
      {error && !editing && <div role="alert" style={{ color: "var(--c-dangerText)", fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {rows === null && <div style={{ ...text.body, color: theme.color.muted }}>Loading…</div>}
      {rows?.length === 0 && <div style={{ ...emptyFrame, padding: 24, textAlign: "center", color: theme.color.muted, fontSize: 13 }}>No agreements recorded.</div>}
      {rows?.map((a) => (
        <div key={a.id} id={`agreement-${a.id}`} style={{ ...card, padding: 0, marginBottom: 10 }}>
          <button type="button" onClick={() => setOpen(open === a.id ? null : a.id)}
            style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 12, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{a.title}</span>
            <span style={{ fontSize: 12, color: theme.color.muted }}>{dpAgreementKindLabel(a.kind)} · {a.counterparty} · {a.status}</span>
          </button>
          {open === a.id && (
            <div style={{ padding: "0 14px 14px" }}>
              <NextSteps items={agreementSteps(a)} action={canEdit ? <button type="button" onClick={() => startEdit(a)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Fill these in</button> : undefined} />
              <DetailFields fields={[
                { label: "Kind", value: dpAgreementKindLabel(a.kind) },
                { label: "Counterparty", value: a.counterparty },
                { label: "Dates", value: [a.startDate ? `from ${fmtDateOnly(a.startDate)}` : null, a.endDate ? `to ${fmtDateOnly(a.endDate)}` : null].filter(Boolean).join(" ") || null },
                { label: "Next review", value: a.reviewAt ? fmtDateOnly(a.reviewAt) : null },
                { label: "Signed copy", value: a.signedDocumentGroupId ? "On file in the Evidence Repository" : null },
                ...(a.kind === "joint_controller_arrangement" ? [
                  { label: "Responsibilities", value: a.responsibilities, wide: true, multiline: true },
                  { label: "Contact point", value: a.contactPoint },
                  { label: "Essence available to data subjects", value: a.essenceAvailable === null ? null : a.essenceAvailable ? "Yes" : "No" },
                ] : []),
                ...(a.kind === "processor_agreement" ? [{ label: "Breach notice window", value: a.breachNoticeHours == null ? null : `${a.breachNoticeHours} hours` }] : []),
                ...(a.destinationCountries.length ? [{ label: "Destinations", value: a.destinationCountries.join(", ") }] : []),
                ...(a.kind === "transfer_risk_assessment" ? [{ label: "Outcome", value: a.assessmentOutcome, wide: true, multiline: true }] : []),
                { label: "Processing activities", value: a.ropaEntries.map((r) => r.name).join(", ") || null, wide: true },
                ...(a.breaches.length ? [{ label: "Breaches", value: a.breaches.map((b) => b.title).join(", "), wide: true }] : []),
              ]} />
              {canEdit && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <EditButton onClick={() => startEdit(a)} />
                  <button type="button" onClick={() => remove(a)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, color: "var(--c-dangerText)" }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {editing && (
        <AgreementFormModal id={editing.id} initial={editing.form} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

/**
 * The agreements register's add / edit form, shared by the Agreements page and the RoPA form ("Add an agreement").
 * It writes only through POST / PATCH /gdpr/agreements (privacy:write on the server, the supplier and signed copy
 * checked to be the organisation's), so an agreement added from a processing activity is the same record.
 */
export function AgreementFormModal({ id, initial, onClose, onSaved }: {
  id: string | null;
  initial: AgreementForm;
  onClose: () => void;
  onSaved: (agreement: { id: string } | null) => void;
}) {
  const [f, setF] = useState<AgreementForm>(initial);
  const [docs, setDocs] = useState<Array<{ documentGroupId: string; fileName: string; version: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<AgreementForm>) => setF((cur) => ({ ...cur, ...p }));
  useEffect(() => {
    api.get<{ documents: Array<{ documentGroupId: string; fileName: string; version: number }> }>("/gdpr/agreements/document-candidates")
      .then((d) => setDocs(d.documents ?? [])).catch(() => setDocs([]));
  }, []);

  async function save() {
    const body = {
      kind: f.kind, title: f.title, counterparty: f.counterparty, status: f.status,
      startDate: f.startDate || null, endDate: f.endDate || null, reviewAt: f.reviewAt || null,
      responsibilities: f.responsibilities || null, contactPoint: f.contactPoint || null, essenceAvailable: f.essenceAvailable,
      breachNoticeHours: f.kind === "processor_agreement" && f.breachNoticeHours !== "" ? Math.round(Number(f.breachNoticeHours)) : null,
      destinationCountries: f.destinationCountries.split(",").map((x) => x.trim()).filter(Boolean),
      assessmentOutcome: f.assessmentOutcome || null, signedDocumentGroupId: f.signedDocumentGroupId || null, notes: f.notes || null,
    };
    setSaving(true); setError(null);
    try {
      const r = id
        ? await api.patch<{ agreement?: { id: string } }>(`/gdpr/agreements/${id}`, body)
        : await api.post<{ agreement?: { id: string } }>("/gdpr/agreements", body);
      onSaved(r?.agreement ?? (id ? { id } : null));
    } catch (e) { setError(apiErrorMessage(e, "Could not save the agreement")); }
    finally { setSaving(false); }
  }

  // Rendered on the page body: opened from the RoPA form, it must not sit inside that <form>, where pressing Enter
  // in one of its fields would submit the processing activity instead.
  return createPortal(
    <ModalOverlay onClose={onClose}>
      <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / -1", fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{id ? "Edit agreement" : "Add agreement"}</div>
        <Field label="Kind" span2={false}>
          <select value={f.kind} onChange={(e) => set({ kind: e.target.value })} style={field}>
            {DP_AGREEMENT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </Field>
        <Field label="Status" span2={false}>
          <select value={f.status} onChange={(e) => set({ status: e.target.value })} style={field}>
            <option value="draft">Draft</option><option value="active">In force</option><option value="ended">Ended</option>
          </select>
        </Field>
        <Field label="Title *" span2={false}><input value={f.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} style={field} /></Field>
        <Field label={f.kind === "processor_agreement" ? "Controller (our customer) *" : "Other party *"} span2={false}>
          <input value={f.counterparty} maxLength={200} onChange={(e) => set({ counterparty: e.target.value })} style={field} />
        </Field>
        <Field label="Start" span2={false}><input type="date" value={f.startDate} onChange={(e) => set({ startDate: e.target.value })} style={field} /></Field>
        <Field label="End" span2={false}><input type="date" value={f.endDate} onChange={(e) => set({ endDate: e.target.value })} style={field} /></Field>
        <Field label="Next review" span2={false}><input type="date" value={f.reviewAt} onChange={(e) => set({ reviewAt: e.target.value })} style={field} /></Field>
        <Field label="Signed copy (Evidence Repository)" span2={false}>
          <select value={f.signedDocumentGroupId} onChange={(e) => set({ signedDocumentGroupId: e.target.value })} style={field}>
            <option value="">None linked</option>
            {f.signedDocumentGroupId && !docs.some((d) => d.documentGroupId === f.signedDocumentGroupId) && <option value={f.signedDocumentGroupId}>The linked document</option>}
            {docs.map((d) => <option key={d.documentGroupId} value={d.documentGroupId}>{d.fileName} (v{d.version})</option>)}
          </select>
        </Field>
        {f.kind === "joint_controller_arrangement" && (
          <>
            <Field label="Who is responsible for what" hint="Notices, data subject requests, breach notification (Art. 26(1)).">
              <textarea rows={3} value={f.responsibilities} onChange={(e) => set({ responsibilities: e.target.value })} style={area} />
            </Field>
            <Field label="Contact point for data subjects" span2={false}><input value={f.contactPoint} onChange={(e) => set({ contactPoint: e.target.value })} style={field} /></Field>
            <Field label="Essence available to data subjects (Art. 26(2))" span2={false}>
              <TriState value={f.essenceAvailable} onChange={(v) => set({ essenceAvailable: v })} />
            </Field>
          </>
        )}
        {f.kind === "processor_agreement" && (
          <Field label="Window to report a breach to the controller (hours)" span2={false}>
            <input type="number" min={1} max={2160} value={f.breachNoticeHours} onChange={(e) => set({ breachNoticeHours: e.target.value })} style={field} />
          </Field>
        )}
        {(f.kind === "transfer_tool" || f.kind === "transfer_risk_assessment") && (
          <Field label="Destination countries (comma-separated)" span2={false}><input value={f.destinationCountries} onChange={(e) => set({ destinationCountries: e.target.value })} style={field} /></Field>
        )}
        {f.kind === "transfer_risk_assessment" && (
          <Field label="Outcome of the assessment">
            <textarea rows={3} value={f.assessmentOutcome} onChange={(e) => set({ assessmentOutcome: e.target.value })} style={area} />
          </Field>
        )}
        <Field label="Notes"><textarea rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} style={area} /></Field>
        {error && <div role="alert" style={{ gridColumn: "1 / -1", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="button" onClick={save} disabled={saving || !f.title.trim() || !f.counterparty.trim()} style={btnPrimary}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
        </div>
      </div>
    </ModalOverlay>,
    document.body,
  );
}
